import { motion } from "framer-motion";
import { profile } from "../data/portfolio";

const easeOut = [0.22, 1, 0.36, 1];

const wordVariants = {
  hidden: { y: "110%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { delay: 0.1 + i * 0.08, duration: 0.9, ease: easeOut },
  }),
};

const Word = ({ children, delay = 0 }) => (
  <span className="inline-block overflow-hidden align-bottom pb-[0.08em]">
    <motion.span
      custom={delay}
      variants={wordVariants}
      initial="hidden"
      animate="show"
      className="inline-block"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  return (
    <section
      id="top"
      className="relative pt-32 pb-24 lg:pt-44 lg:pb-32"
      data-testid="hero-section"
    >
      <div className="container-portfolio">
        {/* Meta row */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.05, ease: easeOut }}
          className="grid grid-cols-12 gap-6 mb-12 lg:mb-20"
        >
          <div className="col-span-12 lg:col-span-4 flex items-center gap-3">
            <span className="h-1.5 w-1.5 rounded-full bg-[#FF3B30] animate-pulse" />
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/70">
              {profile.availability}
            </p>
          </div>
        </motion.div>

        {/* Mobile-only portrait — sits above the name on small screens */}
        <motion.img
          src="/projects/portrait.png"
          alt="Hand-drawn portrait of Kavyasree"
          initial={{ opacity: 0, scale: 0.7, rotate: -10 }}
          animate={{ opacity: 1, scale: 1, rotate: [-4, 0, -4] }}
          transition={{
            opacity: { delay: 0.2, duration: 0.9, ease: easeOut },
            scale: { delay: 0.2, duration: 0.9, ease: easeOut },
            rotate: {
              delay: 1.1,
              duration: 6,
              ease: "easeInOut",
              repeat: Infinity,
            },
          }}
          className="md:hidden block mb-6 w-28 h-auto select-none drop-shadow-[0_4px_14px_rgba(0,0,0,0.08)]"
          draggable={false}
        />

        {/* Display heading */}
        <h1
          className="relative font-black tracking-[-0.04em] leading-[0.86] text-[14vw] sm:text-[12vw] lg:text-[10.5vw]"
          data-testid="hero-heading"
        >
          {/* Hand-drawn portrait — floats to the left of the name */}
          <motion.img
            src="/projects/portrait.png"
            alt="Hand-drawn portrait of Kavyasree"
            initial={{ opacity: 0, scale: 0.6, rotate: 18, y: 30 }}
            animate={{
              opacity: 1,
              scale: 1,
              rotate: [6, 2, 6],
              y: [0, -12, 0],
            }}
            transition={{
              opacity: { delay: 0.2, duration: 0.9, ease: easeOut },
              scale: { delay: 0.2, duration: 0.9, ease: easeOut },
              rotate: {
                delay: 1.1,
                duration: 6.5,
                ease: "easeInOut",
                repeat: Infinity,
              },
              y: {
                delay: 1.1,
                duration: 5.5,
                ease: "easeInOut",
                repeat: Infinity,
              },
            }}
            whileHover={{ scale: 1.06, rotate: -4 }}
            className="hidden md:block absolute right-[-1vw] lg:right-[2vw] bottom-[2vw] lg:bottom-[3vw] w-[15vw] lg:w-[13vw] pointer-events-auto select-none z-10 drop-shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
            data-testid="hero-portrait"
            draggable={false}
          />

          <span className="block relative z-0">
            <Word delay={0}>{profile.firstName}</Word>
            <span className="mx-3 lg:mx-5 inline-block align-middle">
              <motion.span
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.8, ease: easeOut }}
                className="block h-[0.35em] w-[0.35em] rounded-full bg-[#FF3B30]"
              />
            </span>
            <Word delay={1}>{profile.lastName}</Word>
          </span>
          <span className="block text-black/30 italic font-light">
            <Word delay={2}>designs</Word>{" "}
            <Word delay={3}>calm</Word>{" "}
            <Word delay={4}>software.</Word>
          </span>
        </h1>

        {/* Bottom grid: intro + stats */}
        <div className="grid grid-cols-12 gap-8 lg:gap-12 mt-16 lg:mt-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease: easeOut }}
            className="col-span-12 lg:col-span-5 lg:col-start-2"
          >
            <p className="text-lg lg:text-xl font-light leading-relaxed text-black/80 text-balance">
              {profile.tagline}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#work"
                className="inline-flex items-center gap-2 bg-black text-[#f4f4f0] text-sm font-medium px-5 py-3 hover:bg-[#FF3B30] transition-colors"
                data-testid="hero-cta-work"
              >
                See selected work
                <span aria-hidden>↓</span>
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 text-sm font-medium text-black border-b border-black/30 pb-0.5 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
                data-testid="hero-cta-contact"
              >
                Or say hello →
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8, ease: easeOut }}
            className="col-span-12 lg:col-span-4 lg:col-start-9"
          >
            <div className="grid grid-cols-3 gap-6">
              {profile.stats.map((s) => (
                <div key={s.label} data-testid={`hero-stat-${s.label}`}>
                  <div className="text-3xl lg:text-4xl font-black tracking-tighter">
                    {s.value}
                  </div>
                  <div className="mt-2 text-[11px] font-bold tracking-[0.18em] uppercase text-black/50 leading-snug">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-black/15">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-black/50">
                Based in
              </p>
              <p className="mt-1 text-sm font-medium">{profile.location}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
